import { useEffect, useRef, useState, useCallback } from "react";

// Career as a training run. Order matches the CV; epoch numbers run
// oldest (1) -> latest (6) so the "training" metaphor reads naturally.
const experiences = [
  {
    role: "Coding Instructor (concurrent)",
    company: "BrightChamps", period: "Oct 2020 – present",
    epoch: "epoch 6/6", accuracy: "98%", loss: "0.02",
    points: [
      "Mentored student capstone projects in Python and data analytics.",
      "Delivered 250+ live sessions across Python, Machine Learning, Web Development, and TinkerCAD.",
      "Maintained strong CSAT while continuing this role alongside every subsequent promotion.",
    ],
  },
  {
    role: "Evaluation Lead & Analytics / Operations Associate",
    company: "BrightChamps", period: "Dec 2023 – May 2026",
    epoch: "epoch 5/6", accuracy: "95%", loss: "0.05",
    points: [
      "Built reporting automation with Python, Google Sheets & Apps Script — reducing manual effort 50–70%.",
      "Designed evaluation frameworks covering thousands of tutor-student sessions.",
      "Delivered KPI dashboards and weekly insight reports for senior leadership.",
      "Drove operational analytics and process improvements across multiple functions.",
    ],
  },
  {
    role: "Team Lead — Trainer Operations",
    company: "BrightChamps", period: "May 2022 – Oct 2023",
    epoch: "epoch 4/6", accuracy: "88%", loss: "0.12",
    points: [
      "Led teams of 20–30 trainers across multiple learning cohorts.",
      "Built structured performance-monitoring frameworks using quantitative and qualitative metrics.",
      "Implemented analytics-driven upskilling initiatives for trainer development.",
    ],
  },
  {
    role: "Senior Trainer",
    company: "BrightChamps", period: "Apr 2021 – Nov 2023",
    epoch: "epoch 3/6", accuracy: "80%", loss: "0.20",
    points: [
      "Trained and mentored 500+ tutors in Python, Machine Learning, Web Development, and TinkerCAD.",
      "Developed ML and image-processing curriculum modules.",
      "Analysed learner data to improve engagement and completion rates.",
    ],
  },
  {
    role: "HR Associate",
    company: "BrightChamps", period: "Jan 2021 – Apr 2021",
    epoch: "epoch 2/6", accuracy: "68%", loss: "0.33",
    points: [
      "Managed tutor hiring, screening, interviewing, and onboarding processes.",
      "Built attrition tracking and workforce-planning analytics.",
      "Promoted to Senior Trainer within four months based on performance.",
    ],
  },
  {
    role: "Desktop Support Engineer (L1 & L2)",
    company: "HCL Technologies", period: "Dec 2018 – Nov 2019",
    epoch: "epoch 1/6", accuracy: "55%", loss: "0.45",
    points: [
      "Supported 100+ enterprise devices across hardware, software, and networking.",
      "Reduced ticket-resolution time by 20% through streamlined diagnostics.",
    ],
  },
];

const MLCard = ({ exp }) => {
  const wrapperRef = useRef(null);
  const accBar = useRef(null);
  const lossBar = useRef(null);
  const seen = useRef(false);
  const [phase, setPhase] = useState("idle");

  const train = useCallback(() => {
    setPhase("training");
    if (accBar.current) accBar.current.style.width = exp.accuracy;
    if (lossBar.current) lossBar.current.style.width = `${parseFloat(exp.loss) * 100}%`;
    setTimeout(() => setPhase("done"), 1400);
  }, [exp.accuracy, exp.loss]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !seen.current) {
            seen.current = true;
            train();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [train]);

  return (
    <div ref={wrapperRef} className="exp-item">
      <div className="exp-console">
        <div className="exp-console-head">
          <span className="dot r" /><span className="dot y" /><span className="dot g" />
          <span className="exp-file">career_model.py</span>
        </div>
        <div className="exp-console-body">
          <div className="exp-cmd"><span className="muted">$</span> python train.py --role="{exp.role}"</div>
          <div className="exp-cmd"><span className="ep">{exp.epoch}</span> <span className="muted">— {exp.company} · {exp.period}</span></div>

          <div className="exp-metric">
            <span className="exp-mlabel">accuracy</span>
            <span className="exp-bar"><i ref={accBar} className="exp-bar-acc" /></span>
            <span className="exp-mval acc">{phase === "done" ? exp.accuracy : phase === "training" ? "…" : "0%"}</span>
          </div>
          <div className="exp-metric">
            <span className="exp-mlabel">loss</span>
            <span className="exp-bar"><i ref={lossBar} className="exp-bar-loss" /></span>
            <span className="exp-mval loss">{phase === "done" ? exp.loss : phase === "training" ? "…" : "1.0"}</span>
          </div>

          <div className="exp-status">
            {phase === "idle" && <span className="muted">▶ waiting to train…</span>}
            {phase === "training" && <span className="warn">⟳ training in progress…</span>}
            {phase === "done" && <span className="ok">✓ model trained successfully</span>}
          </div>
        </div>
      </div>

      <div className={`exp-card ${phase === "done" ? "revealed" : ""}`}>
        <div className="exp-role">{exp.role}</div>
        <div className="exp-co">{exp.company} · <span className="exp-period">{exp.period}</span></div>
        <ul className="exp-points">
          {exp.points.map((p, i) => (<li key={i}>{p}</li>))}
        </ul>
      </div>
    </div>
  );
};

const Experience = () => {
  return (
    <div className="sec" id="experience">
      <div className="wrap">
        <div className="sec-h"><span className="hash">##</span> training data — career log</div>
        <p className="exp-sub" data-aos="fade-up">scroll to train the model · each role = one epoch</p>

        <div className="exp-summary" data-aos="fade-up">
          <div><span className="es-l">model</span><span className="es-v">CareerNet_v6</span></div>
          <div><span className="es-l">epochs</span><span className="es-v">6</span></div>
          <div><span className="es-l">final acc</span><span className="es-v acc">98%</span></div>
          <div><span className="es-l">promotions</span><span className="es-v">4× ↑</span></div>
          <div><span className="es-l">status</span><span className="es-v ok">● deployed</span></div>
        </div>

        <div className="exp-timeline">
          {experiences.map((exp, i) => (<MLCard key={i} exp={exp} />))}
        </div>
      </div>
    </div>
  );
};

export default Experience;